import React, { ChangeEvent, useRef, useState } from 'react';
import Link from '@splunk/react-ui/Link';
import { ThemeProvider, useLegacyThemeServiceKey } from '../../theme/ThemeProvider';
import { Link as LinkType, SuggestionEntry, resolveLink } from '../dashboard/DrilldownButton';
import { QuestionTooltip } from '../dashboard/QuestionTooltip';
import Popover from '@splunk/react-ui/Popover';
import { AngularInjector } from '../../AngularUtils';
import { DrilldownSuggestionContainer } from './DrilldownSuggestionContainer';

type DrilldownFilters = {
    time: {
        start: string;
        end: string;
        relative: boolean;
    };
    variables: Array<{
        property: string;
        value: string | string[];
    }>;
};

type FilterSuggestions = Array<SuggestionEntry>;

type DrilldownControlGroupProps = {
    disabled: boolean;
    drilldownFilters: DrilldownFilters;
    link: LinkType | undefined;
    onLinkChange: (link: LinkType) => void;
};

const insertVariableAtPosition = (variable: string, position: number, text: string): string =>
    `${text.slice(0, position)}{{{${variable}}}}${text.slice(position)}`;

const getPropertyValueForUrl = (value: string | string[]): string => {
    if (!Array.isArray(value)) {
        return value;
    }
    if (value.length === 1) {
        return value[0];
    }
    return JSON.stringify(value);
};

export const filtersToSuggestions = (filters: DrilldownFilters): FilterSuggestions => {
    const variablesSuggestions = filters?.variables?.reduce<FilterSuggestions>(
        (
            varSuggestions: FilterSuggestions,
            currentVariable: { property: string; value: string | string[] }
        ) => {
            if (varSuggestions.some((suggestion) => suggestion.name === currentVariable.property)) {
                return varSuggestions;
            }
            return varSuggestions.concat({
                name: currentVariable.property,
                value: getPropertyValueForUrl(currentVariable.value),
            });
        },
        []
    );

    const timeSuggestions = filters.time
        ? [
              {
                  name: filters?.time?.relative ? 'startTime' : 'startTimeUTC',
                  value: filters?.time?.start,
              },
              {
                  name: filters?.time?.relative ? 'endTime' : 'endTimeUTC',
                  value: filters?.time?.end,
              },
          ]
        : [];

    return variablesSuggestions?.concat(timeSuggestions);
};

export const DrilldownControlGroup = ({
    disabled,
    drilldownFilters,
    link,
    onLinkChange,
}: DrilldownControlGroupProps): React.JSX.Element => {
    const themeKey = useLegacyThemeServiceKey();
    const dashboardUtil = AngularInjector.useInjectedClass('dashboardUtil');
    const { resolveUrlWithVariables } = dashboardUtil;

    const linkUrl = link ? resolveLink(link) : '';
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);

    const suggestions = filtersToSuggestions(drilldownFilters);
    const outputUrl = resolveUrlWithVariables(linkUrl, suggestions);

    const handleOpen = (): void => {
        if (suggestions?.length) {
            setSuggestionsOpen(true);
        }
    };
    const handleRequestClose = (): void => setSuggestionsOpen(false);

    const onLabelChange = (e: ChangeEvent<HTMLInputElement>): void => {
        link
            ? onLinkChange({
                  ...link,
                  label: e.target.value,
              })
            : onLinkChange({
                  url: '',
                  type: 'StaticExternal',
                  label: e.target.value,
              });
    };

    const onUrlChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
        const inputUrl = e.target.value;
        const inputUrlWithValues = resolveUrlWithVariables(inputUrl, suggestions);

        try {
            const url = new URL(inputUrlWithValues);
            if (url.hostname === window.location.hostname) {
                return onLinkChange({
                    label: link?.label || '',
                    type: 'StaticInternal',
                    path: inputUrl,
                });
            }

            return onLinkChange({
                label: link?.label || '',
                type: 'StaticExternal',
                url: inputUrl,
            });
        } catch {
            // treat links beggining with / or #/ as internal
            if (inputUrl.startsWith('/') || inputUrl.startsWith('#/')) {
                return onLinkChange({
                    label: link?.label || '',
                    type: 'StaticInternal',
                    path: inputUrl,
                });
            }

            // if the user inserted nonsense url, treat as external and leave as is
            return onLinkChange({
                label: link?.label || '',
                type: 'StaticExternal',
                url: inputUrl,
            });
        }
    };

    const insertVariable = (variableName: string): void => {
        const position = textAreaRef.current?.selectionStart || 0;

        switch (link?.type) {
            case 'StaticInternal':
                return onLinkChange({
                    ...link,
                    path: insertVariableAtPosition(variableName, position, link.path),
                });
            case 'StaticExternal':
                return onLinkChange({
                    ...link,
                    url: insertVariableAtPosition(variableName, position, link.url),
                });
            default:
                return onLinkChange({
                    type: 'StaticExternal',
                    label: '',
                    url: insertVariableAtPosition(variableName, position, ''),
                });
        }
    };

    return (
        <ThemeProvider colorScheme={themeKey}>
            <div className="row">
                <div className="col-sm-12 flex-nav">
                    <div className="option-label">
                        Drilldown link{' '}
                        <QuestionTooltip content="The drilldown link will appear in the chart header on the dashboard" />
                    </div>
                    <div className="flex-auto">
                        <input
                            type="text"
                            className="sfx-input form-control"
                            disabled={disabled}
                            maxLength={64}
                            onChange={onLabelChange}
                            placeholder="Link text"
                            value={link?.label || ''}
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-sm-12 flex-nav">
                    <div className="option-label"></div>
                    <div className="flex-auto">
                        <textarea
                            ref={textAreaRef}
                            rows={5}
                            className="sfx-input form-control"
                            style={{ resize: 'none' }}
                            disabled={disabled}
                            onChange={onUrlChange}
                            onFocus={handleOpen}
                            placeholder="Link URL"
                            value={linkUrl}
                        ></textarea>
                        <Popover
                            defaultPlacement="left"
                            open={suggestionsOpen}
                            anchor={textAreaRef.current}
                            onRequestClose={handleRequestClose}
                        >
                            <DrilldownSuggestionContainer
                                suggestions={suggestions}
                                onClick={insertVariable}
                            />
                        </Popover>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-sm-12 flex-nav">
                    <div className="option-label"></div>
                    <div className="flex-auto">
                        {link?.label && outputUrl && (
                            <span>
                                Preview:{' '}
                                <Link openInNewContext={true} to={outputUrl}>
                                    {link.label}
                                </Link>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </ThemeProvider>
    );
};
